import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { getApiUrl, getAuthUrl, PRODUCT_URLS } from '@podus/utils';

/** `/ab/{code}` is mounted at the API root, ahead of `/api`, so strip that suffix. */
const apiOrigin = () => (import.meta.env.VITE_PODUS_API_URL ?? getApiUrl()).replace(/\/api\/?$/, '');

/**
 * `podus.ai/ab/{code}` — the ambassador referral link.
 *
 * The API does the real work: it records the click, sets its own first-party
 * attribution cookie, and tells us whether the code resolved. We then hand the
 * visitor to the auth service with `?ref={code}` appended, because
 * auth.podus.org sits on a different root domain and cannot read a podus.ai
 * cookie — the query parameter is what actually carries the attribution across.
 *
 * An unknown, retired or suspended code is indistinguishable from a typo: the
 * visitor just lands on the Podus AI homepage. No error, no signal.
 */
export default function Referral() {
    const { code } = useParams<{ code: string }>();
    const [tooSlow, setTooSlow] = useState(false);
    // React Strict Mode double-invokes effects in dev; without this guard the
    // handoff would fire (and the click would be recorded) twice.
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        const slowTimer = window.setTimeout(() => setTooSlow(true), 1200);

        const target = `${PRODUCT_URLS.chatpodusai}`;
        const goHome = () => {
            window.location.replace('/');
        };

        const handoff = (resolvedCode: string) => {
            const url = new URL(`${getAuthUrl()}/sign-up`);
            url.searchParams.set('redirect', target);
            url.searchParams.set('ref', resolvedCode);
            window.location.replace(url.toString());
        };

        if (!code) {
            goHome();
            return;
        }

        // No `credentials: include` here: the API answers with a wildcard CORS
        // origin, which browsers refuse to pair with credentialed requests. The
        // `?ref=` parameter below is what actually carries the attribution, so
        // the API's own cookie is never needed on this path.
        fetch(`${apiOrigin()}/ab/${encodeURIComponent(code)}?format=json`, {
            headers: { Accept: 'application/json' },
        })
            .then((res) => res.json())
            .then((body) => {
                const payload = body?.response ?? body;
                if (payload?.resolved && payload?.code) {
                    handoff(String(payload.code));
                } else {
                    goHome();
                }
            })
            .catch(goHome)
            .finally(() => window.clearTimeout(slowTimer));

        return () => window.clearTimeout(slowTimer);
    }, [code]);

    return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-white px-6 text-center">
            <img src="/logo.svg" alt="Podus" className="h-10 w-auto" />
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-primary" />
            <p className="text-sm text-[#747474]">{tooSlow ? 'Almost there…' : 'Taking you to Podus AI…'}</p>
        </div>
    );
}
